import { twMerge } from '@panwds/tailwindcss';
import { format, isEqual, isValid, startOfDay } from 'date-fns';
import React, { useCallback, useEffect, useState, version } from 'react';
import { useIntl } from 'react-intl';
import { noop } from '../../../utils/noop.js';
import { Button } from '../../Button/Button.js';
import { MenuIcon } from '../../Icon/SemanticIcons.js';
import {} from '../../Select/select.types.js';
import { Calendar2 } from '../calendar/calendar.js';
import { DateInput2 } from '../date_input/date_input.js';
import { useActiveDate2 } from '../hooks/use_active_date.js';
import { useDateRangeState2 } from '../hooks/use_date_range_state.js';
import { MAX_DATE, MIN_DATE } from '../utilities/constants.js';
import { dateRangeValidator, dateTimeRangeValidator, getAutoCompleteTime, getDisabledStateForDTRP, getFormatDateTime, getValidationAndExtractDateRange, getValidationAndExtractTimeRange, isTimeValidWithFormat, layoutItemsModifier } from '../utilities/date2.logic.js';
import { applyMessage, cancelMessage, timeRangeInvalidFormat } from '../utilities/messages.js';
import { DateRangePickerLayout } from './date_range_picker_layout.js';
/**
 * @deprecated Use the `DateRangePicker` component instead.
 */
export const DateRangePicker2 = ({ dataMetrics, dataTestId = 'date-range-picker', initialActiveDate = startOfDay(new Date()), initialDateRange, layoutItems, max = MAX_DATE, min = MIN_DATE, onApply = noop, onCancel = noop, weekStartsOn = 0, disabled = false, timeCardOption, timeFormat = 'HH:mm:ss', dateFormat = 'MM/dd/yyyy', splitter = 'to', dateInputPlaceHolder, label }) => {
    const intl = useIntl();
    const [activeDate, setActiveDate] = useActiveDate2({
        initialActiveDate: initialDateRange?.end && isValid(initialDateRange.end)
            ? initialDateRange.end
            : initialActiveDate && isValid(initialActiveDate)
                ? initialActiveDate
                : new Date(),
        max,
        min
    });
    const [isOpen, setOpen] = useState(false);
    const [timeRange, setTimeRange] = useState({
        startTime: '',
        endTime: ''
    });
    const [finalDateRange, setFinalDateRange] = useState();
    const [timeRangeError, setTimeRangeError] = useState();
    const [inputValueError, setInputError] = useState();
    const { selectDate, setEndDate, setStartDate, dateRange } = useDateRangeState2(undefined, min, max);
    const [hoverDate, setHoverDate] = useState();
    const [dateRangeSelected, setDateRangeSelected] = useState();
    const [durationSelected, setDurationSelected] = useState(timeCardOption?.durations?.[0] || { value: '' });
    useEffect(() => {
        const dateUpdateNeeded = initialDateRange?.start &&
            initialDateRange?.end &&
            isValid(initialDateRange.start) &&
            isValid(initialDateRange.end) &&
            (dateRange.start && dateRange.end && isValid(dateRange.start) && isValid(dateRange.end)
                ? !(isEqual(dateRange.start, initialDateRange.start) && isEqual(dateRange.end, initialDateRange.end))
                : true);
        if (dateUpdateNeeded) {
            try {
                const dateString = getFormatDateTime({
                    startDate: format(initialDateRange.start, dateFormat),
                    endDate: format(initialDateRange.end, dateFormat),
                    ...(timeCardOption && {
                        startTime: format(initialDateRange.start, timeFormat),
                        endTime: format(initialDateRange.end, timeFormat)
                    })
                }, splitter);
                setFinalDateRange({ start: initialDateRange.start, end: initialDateRange.end });
                onValidateInputValue(dateString);
                setStartDate(initialDateRange.start);
                setEndDate(initialDateRange.end);
                setActiveDate(initialDateRange.end);
                setInputError(undefined);
                timeCardOption &&
                    setTimeRange({
                        startTime: format(initialDateRange.start, timeFormat),
                        endTime: format(initialDateRange.end, timeFormat)
                    });
            }
            catch (e) {
                setInputError({ error: intl.formatMessage(e) });
            }
        }
    }, [initialDateRange, min, max]);
    useEffect(() => {
        try {
            validateChangeCalendarRange();
            setTimeRangeError({ error: undefined });
        }
        catch (e) {
            setTimeRangeError({ error: intl.formatMessage(e) });
        }
    }, [dateRange.start, dateRange.end]);
    const validateChangeCalendarRange = () => {
        if (timeCardOption &&
            timeRange.startTime &&
            timeRange.endTime &&
            isValid(dateRange.start) &&
            isValid(dateRange.end)) {
            const dateTimeString = getFormatDateTime({
                startDate: format(dateRange.start, dateFormat),
                endDate: format(dateRange.end, dateFormat),
                startTime: timeRange.startTime,
                endTime: timeRange.endTime
            }, splitter);
            timeCardOption && dateTimeRangeValidator(dateTimeString, max, min, splitter, dateFormat, timeFormat);
            !timeCardOption && dateRangeValidator(dateTimeString, max, min, splitter, dateFormat);
        }
    };
    const handleSelectDate = (next) => {
        if (!next) {
            return;
        }
        selectDate(next);
        setActiveDate(next);
        setHoverDate(undefined);
    };
    const onApplyClick = () => {
        let startTime = timeRange.startTime || '';
        let endTime = timeRange.endTime || '';
        if (!dateRange.start || !dateRange.end || (!!timeCardOption && (!startTime || !endTime))) {
            return;
        }
        const startDate = format(dateRange.start, dateFormat);
        const endDate = format(dateRange.end, dateFormat);
        if (!timeRange.startTime) {
            startTime = format(new Date(`${startDate} 00:00:00`), timeFormat);
        }
        if (!endTime) {
            endTime = format(new Date(`${endDate} 23:59:59`), timeFormat);
        }
        const start = new Date(`${startDate} ${startTime}`);
        const end = new Date(`${endDate}  ${endTime}`);
        setStartDate(start);
        setEndDate(end);
        setDateRangeSelected(undefined);
        setDurationSelected(timeCardOption?.durations?.[0] || { value: '' });
        setFinalDateRange({ start, end });
        setInputError({ error: undefined });
        setTimeRangeError(undefined);
        onApply({ start, end });
    };
    const onValidateInputValue = (inputValue) => {
        timeCardOption && dateTimeRangeValidator(inputValue, max, min, splitter, dateFormat, timeFormat);
        !timeCardOption && dateRangeValidator(inputValue, max, min, splitter, dateFormat);
    };
    const onDateInputSubmit = (dateTimeString) => {
        try {
            onValidateInputValue(dateTimeString);
            setInputError({ error: undefined });
            setTimeRangeError(undefined);
            const { isValid: isValidDate, startDate, endDate } = getValidationAndExtractDateRange(dateTimeString, splitter, dateFormat);
            const { isValid: isValidTime, startTime, endTime } = getValidationAndExtractTimeRange(dateTimeString, splitter, timeFormat);
            if (!isValidDate || (!!timeCardOption && !isValidTime)) {
                return;
            }
            let startDateTime;
            let endDateTime;
            if (!startTime) {
                startDateTime = new Date(`${startDate} 00:00:00`);
            }
            else {
                startDateTime = new Date(`${startDate} ${startTime}`);
            }
            if (!endTime) {
                endDateTime = new Date(`${endDate} 23:59:59`);
            }
            else {
                endDateTime = new Date(`${endDate}  ${endTime}`);
            }
            if (finalDateRange?.start &&
                finalDateRange?.end &&
                isEqual(startDateTime, finalDateRange?.start) &&
                isEqual(endDateTime, finalDateRange.end)) {
                return;
            }
            setStartDate(startDateTime);
            setEndDate(endDateTime);
            setActiveDate(endDateTime);
            timeCardOption && setTimeRange({ startTime: startTime, endTime: endTime });
            setFinalDateRange({ start: startDateTime, end: endDateTime });
            onApply({ start: startDateTime, end: endDateTime });
        }
        catch (e) {
            setInputError({ error: intl.formatMessage(e) });
        }
    };
    const onValidateTimeRange = (partialTimeRange) => {
        let dateTimeString = '';
        if (partialTimeRange.startTime && partialTimeRange.endTime && isValid(dateRange.start) && isValid(dateRange.end)) {
            dateTimeString = getFormatDateTime({
                startDate: format(dateRange.start, dateFormat),
                endDate: format(dateRange.end, dateFormat),
                startTime: partialTimeRange.startTime,
                endTime: partialTimeRange.endTime
            }, splitter);
        }
        else if (timeRange.startTime && partialTimeRange.endTime && isValid(dateRange.start) && isValid(dateRange.end)) {
            dateTimeString = getFormatDateTime({
                startDate: format(dateRange.start, dateFormat),
                endDate: format(dateRange.end, dateFormat),
                startTime: timeRange.startTime,
                endTime: partialTimeRange.endTime
            }, splitter);
        }
        else if (partialTimeRange.startTime && timeRange.endTime && isValid(dateRange.start) && isValid(dateRange.end)) {
            dateTimeString = getFormatDateTime({
                startDate: format(dateRange.start, dateFormat),
                endDate: format(dateRange.end, dateFormat),
                startTime: partialTimeRange.startTime,
                endTime: timeRange.endTime
            }, splitter);
        }
        if (dateTimeString) {
            dateTimeRangeValidator(dateTimeString, max, min, splitter, dateFormat, timeFormat);
        }
    };
    const validateTimeRangeFormat = (partialTimeRange) => {
        let isValid = true;
        if (partialTimeRange.startTime && !isTimeValidWithFormat(partialTimeRange.startTime, timeFormat)) {
            isValid = false;
        }
        else if (partialTimeRange.endTime && !isTimeValidWithFormat(partialTimeRange.endTime, timeFormat)) {
            isValid = false;
        }
        if (!isValid) {
            throw timeRangeInvalidFormat;
        }
    };
    function handleLayoutOptionSelect(value) {
        const itemFound = layoutItemsModifier(layoutItems).find(({ value: itemValue }) => value === itemValue);
        let start;
        let end;
        if (value === 'all') {
            start = min;
            end = max;
        }
        else if (value === 'customs') {
            setDateRangeSelected(value);
            return;
        }
        else {
            start = itemFound?.dateRange?.startDate;
            end = itemFound?.dateRange?.endDate;
        }
        if (!start || !end) {
            return;
        }
        setStartDate(start);
        setEndDate(end);
        onApply({ start, end });
        setFinalDateRange({ start, end });
        setActiveDate(end);
        setDateRangeSelected(undefined);
        setDurationSelected(timeCardOption?.durations?.[0] || { value: '' });
        timeCardOption &&
            setTimeRange({
                startTime: format(start, timeFormat),
                endTime: format(end, timeFormat)
            });
        setInputError({ error: undefined });
        setTimeRangeError(undefined);
        setOpen(false);
    }
    const onChangeTimeHandler = (value) => {
        try {
            validateTimeRangeFormat({
                startTime: value.startTime || timeRange.startTime || '',
                endTime: value.endTime || timeRange.endTime || ''
            });
            const autoTimeCalculate = getAutoCompleteTime({
                startTime: value.startTime || '',
                endTime: value.endTime || '',
                durationSelected,
                timeFormat,
                timeRange
            });
            setTimeRange(autoTimeCalculate);
            onValidateTimeRange(autoTimeCalculate);
            setTimeRangeError({ error: undefined });
        }
        catch (e) {
            setTimeRangeError({ error: intl.formatMessage(e) });
        }
    };
    const togglePopover = useCallback((e) => {
        // Between react 16 and 17 event propagation changed such that 17 and after we need to stop propagation of this event,
        // while for 16 we need to let the event propagate.
        if (!/^16/.test(version)) {
            e.stopPropagation();
        }
        setOpen(!isOpen);
    }, [isOpen, setOpen]);
    const suffix = (React.createElement("button", { type: "button", onMouseDown: togglePopover, "data-testid": `${dataTestId}-menu-icon`, "data-metrics": dataMetrics, className: twMerge('twds-flex twds-items-center twds-border-none twds-bg-transparent twds-h-full twds-p-0 twds-pr-2 twds-outline-none', !disabled
            ? 'dark:twds-text-white hover:twds-cursor-pointer'
            : 'twds-text-disabled dark:twds-text-disabled hover:twds-cursor-not-allowed') },
        React.createElement(MenuIcon, { className: twMerge(!disabled
                ? 'dark:twds-text-white hover:twds-cursor-pointer'
                : 'twds-text-disabled dark:twds-text-disabled hover:twds-cursor-not-allowed'), isOpen: isOpen, buttonType: "default" })));
    const onCancelHandler = () => {
        setActiveDate(finalDateRange?.end && isValid(finalDateRange.end)
            ? finalDateRange.end
            : initialActiveDate && isValid(initialActiveDate)
                ? initialActiveDate
                : new Date());
        setStartDate(finalDateRange?.start);
        setEndDate(finalDateRange?.end);
        setHoverDate(undefined);
        setTimeRange({
            startTime: finalDateRange?.start && timeCardOption ? format(finalDateRange?.start, timeFormat) : '',
            endTime: finalDateRange?.end && timeCardOption ? format(finalDateRange?.end, timeFormat) : ''
        });
        setTimeRangeError(undefined);
        onCancel();
        setDateRangeSelected(undefined);
        setDurationSelected(timeCardOption?.durations?.[0] || { value: '' });
        setOpen(false);
    };
    return (React.createElement(DateRangePickerLayout, { dataMetrics: dataMetrics, dataTestId: `${dataTestId}-layout`, open: isOpen, onCancel: onCancelHandler, items: layoutItems, durationSelected: durationSelected, setDurationSelected: setDurationSelected, inputs: React.createElement(DateInput2, { inputProps: { suffix, dataMetrics, dataTestId: `${dataTestId}-date-input` }, dataMetrics: dataMetrics, label: label, dataTestId: `${dataTestId}-date-input-forms`, disabled: disabled, value: getFormatDateTime({
                startDate: finalDateRange?.start ? format(finalDateRange?.start, dateFormat) : '',
                endDate: finalDateRange?.end ? format(finalDateRange?.end, dateFormat) : '',
                ...(timeCardOption
                    ? {
                        startTime: finalDateRange?.start ? format(finalDateRange?.start, timeFormat) : '',
                        endTime: finalDateRange?.end ? format(finalDateRange?.end, timeFormat) : ''
                    }
                    : {})
            }, splitter), handleOnSubmit: onDateInputSubmit, placeHolder: dateInputPlaceHolder, errors: inputValueError }), actions: React.createElement(React.Fragment, null,
            React.createElement(Button, { dataTestId: `${dataTestId}-cancel-button`, dataMetrics: dataMetrics, onClick: onCancelHandler, disabled: disabled }, intl.formatMessage(cancelMessage)),
            React.createElement(Button, { appearance: "primary", dataTestId: `${dataTestId}-apply-button`, dataMetrics: dataMetrics, disabled: getDisabledStateForDTRP(finalDateRange, dateRange, timeRange, timeRangeError, dateFormat, timeFormat, timeCardOption) || disabled, onClick: () => {
                    onApplyClick();
                    setOpen(false);
                } }, intl.formatMessage(applyMessage))), calendar: React.createElement(Calendar2, { activeDate: activeDate, dataTestId: `${dataTestId}-calendar`, dataMetrics: dataMetrics, hoverDate: hoverDate, max: max, min: min, onActiveDateChange: setActiveDate, onHoverDateChange: setHoverDate, onSelectedDateChange: handleSelectDate, dateRange: dateRange, weekStartsOn: weekStartsOn }), max: max, min: min, selectedOption: dateRangeSelected, onSelectOption: handleLayoutOptionSelect, timeCardOption: timeCardOption, timeRange: timeRange, onChangeTime: onChangeTimeHandler, errors: timeRangeError }));
};
//# sourceMappingURL=date_range_picker.js.map